import { useEffect, useState } from "react";
import { useNavigate, useSearchParams, Link } from "react-router-dom";
import { toast } from "sonner";
import { usePortalAuth } from "@/lib/PortalAuthContext";
import { formatPortalError } from "@/lib/portalApi";
import Logo from "@/components/Logo";

export default function PortalVerify() {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const { verifyMagicLink } = usePortalAuth();
  const [status, setStatus] = useState("verifying"); // verifying | success | error
  const [errorMsg, setErrorMsg] = useState("");

  useEffect(() => {
    const token = params.get("token");
    if (!token) {
      setStatus("error");
      setErrorMsg("Missing sign-in token.");
      return;
    }
    (async () => {
      try {
        await verifyMagicLink(token);
        setStatus("success");
        toast.success("Signed in");
        setTimeout(() => navigate("/portal"), 400);
      } catch (err) {
        setStatus("error");
        setErrorMsg(formatPortalError(err?.response?.data?.detail, "Sign-in failed."));
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <div data-testid="portal-verify-page" className="min-h-screen bg-[#050505] text-white flex flex-col">
      <header className="border-b border-white/10 px-6 md:px-10 h-16 flex items-center">
        <Logo />
      </header>
      <main className="flex-1 flex items-center justify-center px-6 py-14">
        <div className="max-w-md text-center">
          {status === "verifying" && (
            <>
              <div className="text-[#D4AF37] tracking-[0.24em] text-[11px] mb-3">VERIFYING</div>
              <h1 className="font-display text-3xl mb-2" style={{ fontWeight: 300 }}>Signing you in…</h1>
              <p className="text-white/50 text-sm">Just a moment.</p>
            </>
          )}
          {status === "success" && (
            <>
              <div className="text-[#D4AF37] tracking-[0.24em] text-[11px] mb-3">SUCCESS</div>
              <h1 className="font-display text-3xl mb-2" style={{ fontWeight: 300 }}>Welcome back.</h1>
              <p className="text-white/50 text-sm">Redirecting to your portal…</p>
            </>
          )}
          {status === "error" && (
            <>
              <div className="text-red-400 tracking-[0.24em] text-[11px] mb-3">LINK ERROR</div>
              <h1 className="font-display text-3xl mb-2" style={{ fontWeight: 300 }}>We couldn't sign you in.</h1>
              <p className="text-white/60 text-sm mb-6">{errorMsg}</p>
              <Link
                to="/portal/login"
                data-testid="portal-verify-retry"
                className="inline-block bg-[#D4AF37] text-[#050505] font-semibold px-6 py-2.5 rounded-md text-xs tracking-widest hover:bg-[#E5C158]"
              >REQUEST A NEW LINK</Link>
            </>
          )}
        </div>
      </main>
    </div>
  );
}
